import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
    DistributionDocument,
    DistributionDocumentState,
    DistributionJob,
    DistributionJobChannel,
    DistributionJobChannels,
    DistributionJobItem,
    DistributionJobState,
    InsignState,
    isInSignDistributionJob,
    isPrintDistributionJob
} from '../../models/distribution-doc-models';
import {
    CaseDistributionJobUriBuilderParams,
    DistributionJobUriBuilderParams,
    DistributionService,
    WorkflowDistributionJobUriBuilderParams
} from '@app/core/services/distribution.service';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { Receiver } from '../../models/participant';
import { TranslationEntry } from '../../models/translatable';
import { FileUtilityService } from '@app/core/services/file-utility.service';
import { Thumbnailable } from '../file-upload/thumb-nail-base.component';
import { OP, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { BasePatchComponent } from '../base/base-patch.component';
import { Address } from '../../models/address';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DistributionHeaderComponent } from '@app/shared/components/distribution-job/abstract-header/distribution-header.component';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import * as _ from 'lodash-es';


@Component({
    selector: 'distribution-job',
    templateUrl: './distribution-job.component.html',
    styleUrls: ['./distribution-job.component.scss']
})
export class DistributionJobComponent extends BasePatchComponent<DistributionJob, DistributionJobUriBuilderParams>
                                      implements OnInit {

    protected paramsIdNames = [];
    protected myConfig = { fetchAtInit: false };

    @ViewChild('headerComponent')
    private headerComponent: DistributionHeaderComponent<DistributionJob>;

    @Input()
    public channels: TranslationEntry[];
    
    @Input()
    public receivers: Receiver[];

    @Output()
    public cancel = new EventEmitter<DistributionJob>();

    @Output()
    public prepare = new EventEmitter<DistributionJob>();

    @Output()
    public signNow = new EventEmitter<DistributionJob>();

    @Output()
    public send = new EventEmitter<DistributionJob>();

    @Output()
    public copy = new EventEmitter<DistributionJob>();

    @Output()
    public onCdkFileDropped = new EventEmitter<CdkDragDrop<string[]>>();

    @Output()
    public onOpenTemplatesDialog = new EventEmitter<boolean>();

    public dragging: boolean;

    get job(): DistributionJob {
        return this.updated;
    }

    get DistributionJobState() {
        return DistributionJobState;
    }

    get DistributionJobChannels() {
        return DistributionJobChannels;
    }

    get DistributionJobChannel() {
        return DistributionJobChannel;
    }

    get cancelButtonAvailable(): boolean {
        return this.job.state !== DistributionJobState.SENT;
    }

    get prepareButtonAvailable(): boolean {
        return this.isCaseJob() &&
               !this.job.isInSignSignNow &&
               (!this.job.state ||
                this.job.state === DistributionJobState.INITIAL ||
                this.job.state === DistributionJobState.ERROR_PREPARING);
    }

    get prepareButtonDisabled(): boolean {
        return !this.selectedChannel ||
               (this.selectedChannel === DistributionJobChannels.SIGNING && ((!this.job.documents || !this.job.documents.length) && (!this.job.item || !this.job.item.documents.length))) ||
               (this.selectedChannel !== DistributionJobChannels.PRINT && !this.selectedReceiver) ||
               (this.selectedChannel === DistributionJobChannels.EMAIL && (this.headerComponent && !this.headerComponent.isValid()) )
            ;
    }

    get signNowButtonAvailable(): boolean {
        return this.isCaseJob() &&
               isInSignDistributionJob(this.job) &&
               this.job.isInSignSignNow &&
               this.job.insign_state !== InsignState.FINISHED;
    }

    get signNowButtonDisabled(): boolean {
        return !this.signNowButtonAvailable || !this.job.item.documents.length;
    }

    get sendButtonAvailable(): boolean {
        return this.isCaseJob() &&
               this.selectedChannel !== DistributionJobChannels.PRINT &&
              (this.job.state === DistributionJobState.PREPARED || this.job.state === DistributionJobState.ERROR_SENDING);
    }

    get copyButtonAvailable(): boolean {
        return false; // this.isCaseJob() && this.job.state === DistributionJobState.SENT;
    }

    get isDisabled(): boolean {
        return this.job.state && this.job.state !== DistributionJobState.INITIAL && this.job.state !== DistributionJobState.ERROR_PREPARING;
    }

    public selectedChannel: TranslationEntry;
    public selectedReceiver: Receiver;

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected distributionService: DistributionService,
                protected fileUtilityService: FileUtilityService) {
        super(componentServiceBundle, distributionService);
    }

    public ngOnInit() {
        super.ngOnInit();
        this.selectedChannel = this.channels.find(channel => channel.key === this.original.channel);

        if (this.job.receiver) {
            this.selectedReceiver = this.receivers.find(rec => rec.id === this.job.receiver.id);
        }
    }

    public addDocument(document: DistributionDocument): void {
        this.original.addDocument(document);
        this.updated.addDocument(document);
    }

    public viewDocument(document: Thumbnailable): void {
        if (document && (<DistributionDocument>document).state !== DistributionDocumentState.ERROR) {
            this.fileUtilityService.viewFileInNewTab(document);
        }
    }

    public updateDocument(docToUpdate: DistributionDocument): void {
        this.distributionService.updateDistributionJobDocument(this.generateUriBuilderParams(this.job.item), docToUpdate)
                                    .subscribe(result => {
                                        this.original.updateDocument(result);
                                        this.updated.updateDocument(result);
                                        this.bananaBoxChange.emit(this.job);
                                    }, err => this.handleError(err));
    }

    public removeDocument(docToRemove: DistributionDocument): void {
        const placeholderName = 
            this.translateService.instant(docToRemove.trans.placeholder).toTitleCase() + 
            ': ' + 
            this.translateService.instant(docToRemove.trans.category);

        this.showSimpleConfirmDialog(
            marker('common.dialog.delete.title'),
            `${this.translateService.instant(marker('common.dialog.delete.text'), { name: docToRemove.is_placeholder ? placeholderName : docToRemove.display_name })}`,
            '500px'
        ).subscribe(dialogResult => {
            if (dialogResult) {
                this.distributionService.removeDocumentFromDistributionJob(this.generateUriBuilderParams(this.job.item), docToRemove)
                    .subscribe(result => {
                    this.deleteDocument(docToRemove);
                }, err => this.handleError(err));
            }
        });
    }

    private deleteDocument(docToRemove: DistributionDocument): void {
        if (this.original.workflow_id) {
            this.original.documents = this.original.documents.filter(doc => doc.id !== docToRemove.id);
            this.updated.documents = this.updated.documents.filter(doc => doc.id !== docToRemove.id);
        } else if (this.original.item) {
            this.original.item.documents = this.original.item.documents.filter(doc => doc.id !== docToRemove.id);
            this.updated.item.documents = this.updated.item.documents.filter(doc => doc.id !== docToRemove.id);
        }

        this.bananaBoxChange.emit(this.job);
    }

    public cancelJob(): void {
        this.showSimpleConfirmDialog(
            marker('common.dialog.delete.title'),
            `${this.translateService.instant(marker('common.dialog.delete.text'), { 
                name: this.translateService.instant(marker('detailed_processing.distribution.label.the_job'), { 
                    receiver: this.job.receiver && this.job.receiver.actionListDisplayName ? ' ' + this.job.receiver.actionListDisplayName : '',
                    channel: ' ' + this.job.channel_value 
                })})
            }`,
            '500px'
        ).subscribe(dialogResult => {
            if (dialogResult) {
                this.cancel.emit(this.job);
            }
        });
    }

    public fileDropped(event: CdkDragDrop<string[]>): void {
       this.draggingOver(false);
       this.onCdkFileDropped.emit(event);
    }

    public draggingOver(value: boolean): void {
        this.dragging = value;
    }

    public isCaseJob(): boolean {
        return !this.job.workflow_id;
    }

    private createReceiverPatchesByChannel(channel: DistributionJobChannel, address: Address): OpData[] {
        let opData: OpData[] = [];

        if (channel) {
            const emailAddress = address && address.email;

            if (channel === DistributionJobChannel.EMAIL) {
                opData = opData.concat([{
                    op: emailAddress ? OP.replace : OP.remove,
                    path: 'email_to',
                    value: emailAddress || undefined
                }]);
            }

            if (channel === DistributionJobChannel.SIGNING) {
                const smsPhoneNumber = address && (address.phone || address.phone2);

                opData = opData.concat([{
                    op: OP.replace,
                    path: 'insign_send_email',
                    value: !!emailAddress
                }, {
                    op: emailAddress ? OP.replace : OP.remove,
                    path: 'insign_email_to',
                    value: emailAddress || undefined
                }, {
                    op: OP.replace,
                    path: 'insign_send_sms',
                    value: !!smsPhoneNumber
                }, {
                    op: smsPhoneNumber ? OP.replace : OP.remove,
                    path: 'insign_sms_to',
                    value: smsPhoneNumber || undefined
                }]);
            }
        }

        return opData;
    }

    public updateReceiver(): void {
        if (this.selectedReceiver && (!this.job.receiver || this.job.receiver.id !== this.selectedReceiver.id)) {
            let opData: OpData[] = [{
                op: OP.replace,
                path: this.convertToPatchPath('receiver.id'),
                value: this.selectedReceiver.id
            }];

            if (!this.job.workflow_id) {
                opData = opData.concat([{
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.origin_id'),
                    value: this.selectedReceiver.origin_id
                }, {
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.origin'),
                    value: this.selectedReceiver.origin
                }, {
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.name1'),
                    value: this.selectedReceiver.name1
                }, {
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.name2'),
                    value: this.selectedReceiver.name2
                }, {
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.name3'),
                    value: this.selectedReceiver.name3
                }, {
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.address.phone'),
                    value: this.selectedReceiver.address.phone
                }, {
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.address.phone2'),
                    value: this.selectedReceiver.address.phone2
                }, {
                    op: OP.replace,
                    path: this.convertToPatchPath('receiver.address.email'),
                    value: this.selectedReceiver.address.email
                }]);

                if (this.selectedReceiver.address) {
                    opData = opData.concat([{
                        op: OP.replace,
                        path: this.convertToPatchPath('receiver.address.phone'),
                        value: this.selectedReceiver.address.phone
                    }, {
                        op: OP.replace,
                        path: this.convertToPatchPath('receiver.address.phone2'),
                        value: this.selectedReceiver.address.phone2
                    }, {
                        op: OP.replace,
                        path: this.convertToPatchPath('receiver.address.email'),
                        value: this.selectedReceiver.address.email
                    }]);

                    const receiverPatchesByChannel = this.createReceiverPatchesByChannel(this.job.channel, this.selectedReceiver.address);
                    opData = opData.concat(receiverPatchesByChannel);
                }
            }

            this.patchOpDataArray(opData, () => {
                if (!this.job.receiver) {
                    this.job.receiver = new Receiver();
                }

                this.job.receiver.actionListDisplayName = this.selectedReceiver.actionListDisplayName;
                this.bananaBoxChange.emit(this.job);
            });
        }
    }

    public updateChannel(): void {
        if (this.job.channel !== this.selectedChannel.key) {
            let opData: OpData[] = [{
                op: OP.replace,
                path: 'channel',
                value: this.selectedChannel.key
                }, {
                op: OP.replace,
                path: 'channel_value',
                value: this.translateService.instant(this.selectedChannel.trans)
            }];

            if (this.job.receiver && this.job.receiver.address) {
                const receiverPatchesByChannel = this.createReceiverPatchesByChannel(this.selectedChannel.key as DistributionJobChannel, this.job.receiver.address);
                opData = opData.concat(receiverPatchesByChannel);
            }

            const documents = this.job.documentsArray && this.job.documentsArray.slice() || [];
            this.patchOpDataArray(opData, () => {
                this.verifyDocumentsRemoved(documents);
            });
        }
    }

    protected FAILURES = {
        INSIGN_DOCUMENTS: {
            title_code: marker('validate.documents.removed.insign_title'),
            message_code: marker('validate.documents.removed.insign')
        }
    };

    public verifyDocumentsRemoved(originalDocuments: DistributionDocument[]): void {
        const refreshedDocuments = this.job.documentsArray || [];
        if (originalDocuments.length > refreshedDocuments.length) {
            const removedDocuments = _.filter(originalDocuments, (originalDoc) => {
                return !_.find(refreshedDocuments, (refreshedDoc) => {
                    return originalDoc.id === refreshedDoc.id;
                });
            });

            if (removedDocuments.length) {
                this.showSimpleInfoDialog(this.FAILURES.INSIGN_DOCUMENTS.title_code,
                    this.translateService.instant(this.FAILURES.INSIGN_DOCUMENTS.message_code, {
                        additional_data: _.map(removedDocuments, doc => doc.display_name).join(', ')
                    }),
                    '500px'
                ).subscribe(() => {});
            }
        }
    }

    public prepareJob(): void {
        if (!this.headerComponent || this.headerComponent.validate()) {
            this.prepare.emit(this.job);
        }
    }

    public signJob(): void {
        if (!this.headerComponent || this.headerComponent.validate()) {
            this.signNow.emit(this.job);
        }
    }

    public generateUriBuilderParams(jobItem?: DistributionJobItem): DistributionJobUriBuilderParams {
        if (this.job.workflow_id) {
            return new WorkflowDistributionJobUriBuilderParams(this.job.workflow_id, this.job);
        } else {
            return new CaseDistributionJobUriBuilderParams(jobItem, this.job);
        }
    }

    protected initNew(result: DistributionJob): DistributionJob {
        return DistributionJob.createSubTypeByChannel(result);
    }

    public reInitOriginalAndUpdated(result: DistributionJob): void {
        super.reInitOriginalAndUpdated(result);
    }

    public getPrepareButtonText(): string {
        let btnText = '';
        if (isPrintDistributionJob(this.job)) {
            btnText = marker('detailed_processing.distribution.btn.finish_distribution');
        } else {
            btnText = marker('detailed_processing.distribution.btn.prepare_distribution');
        }

        return this.translateService.instant(btnText).toUpperCase();
    }
}
